/*
Copyright (c) NAVER Corp.
name: @cfcs/vue3
license: MIT
author: NAVER Corp.
repository: https://github.com/naver/cfcs/tree/main/packages/vue3
version: 0.1.0
*/
import { adaptReactive, camelize } from '@cfcs/core';
import { ref, onMounted, onUnmounted, reactive } from 'vue';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

/**
 * @description In Vue 3, you can create reactive components through adapters.
 * @category Reactive
 * @example
 * ```ts
 * import { useReactive } from "@cfcs/vue3";
 *
 * export function useReactiveComponent() {
 *   return useReactive(REACTIVE_ADAPTER);
 * }
 * ```
 */

function useReactive(reactiveAdapter, props) {
  var adaptResult = adaptReactive(reactiveAdapter, props);
  var reactiveState = adaptResult.state();
  var names = Object.keys(reactiveState);
  var refs = {};

  for (var name in reactiveState) {
    refs[name] = ref(reactiveState[name]);
  }

  var methods = adaptResult.methods();
  onMounted(function () {
    adaptResult.mounted();
    var inst = adaptResult.instance();
    names.forEach(function (name) {
      inst.subscribe(name, function (value) {
        refs[name].value = value;
      });
    });
    adaptResult.init();
  });
  onUnmounted(function () {
    adaptResult.destroy();
  });
  var reactiveEvents = adaptResult.events();
  var events = reactiveEvents.reduce(function (eventResult, name) {
    eventResult[camelize("on ".concat(name))] = function (callback) {
      adaptResult.on(name, callback);
      onUnmounted(function () {
        adaptResult.off(name, callback);
      });
    };

    return eventResult;
  }, {});
  return __assign(__assign(__assign({}, refs), methods), events);
}
/**
 * @category Reactive
 * @hidden
 */

function useLegacyReactive(reactiveAdapter, props) {
  var adaptResult = adaptReactive(reactiveAdapter, props);
  var reactiveState = adaptResult.state();
  var names = Object.keys(reactiveState);
  var result;
  var methods = adaptResult.methods();
  onMounted(function () {
    adaptResult.mounted();
    var inst = adaptResult.instance();
    names.forEach(function (name) {
      inst.subscribe(name, function (value) {
        result[name] = value;
      });
    });
    adaptResult.init();
  });
  onUnmounted(function () {
    adaptResult.destroy();
  });
  var reactiveEvents = adaptResult.events();
  var events = reactiveEvents.reduce(function (eventResult, name) {
    eventResult[camelize("on ".concat(name))] = function (callback) {
      adaptResult.on(name, callback);
      onUnmounted(function () {
        adaptResult.off(name, callback);
      });
    };

    return eventResult;
  }, {});
  result = reactive(__assign(__assign(__assign({}, reactiveState), methods), events));
  return result;
}

export { useLegacyReactive, useReactive };
//# sourceMappingURL=cfcs.esm.js.map
